import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import PermissaoBancaWrapper from '../../components/PermissaoBancaWrapper';
import { PermissaoEnum } from '../../model/Usuario/Permissao';
import { TipoBancaNome } from '../../model/banca/Banca';
import useUserStore from '../../stores/userStore';
import Usuario from '../../model/Usuario/Usuario';
import AcoesSecretarioModal from './AcoesSecretarioModal';
import './BancasModal.css';

interface BancaData {
  id: string;
  processoId: string;
  status: string;
  processoNome: string;
  tipoBanca: string;
  membros: string[];
  data: string;
  horaInicial: string;
  horaFinal: string;
  salaDeBanca: string;
  dataFinalizacao?: string;
}

interface BancasModalProps {
  bancas: BancaData[];
  isOpen: boolean;
  onClose: () => void;
}

const BancasModal: React.FC<BancasModalProps> = ({ bancas, isOpen, onClose }) => {
  const navigate = useNavigate();
  const usuario = useUserStore((state) => state.usuario) as Usuario | null;
  const [showAcoesSecretarioModal, setShowAcoesSecretarioModal] = useState(false);
  const [selectedBancaId, setSelectedBancaId] = useState<string>('');
  const [selectedBanca, setSelectedBanca] = useState<BancaData | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isMinimized, setIsMinimized] = useState(true);
  const [isTransitioning, setIsTransitioning] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
    }
  }, [isOpen]);

  const getPermissoesUsuario = (bancaId: string): string[] => {
    if (!usuario?.perfil) return [];
    return usuario.perfil.permissoes.map((p) => p.nome).filter(p => p.includes(`-${bancaId}`));
  };

  const hasPermissionForBanca = (bancaId: string): boolean => {
    return getPermissoesUsuario(bancaId).length > 0;
  };

  const bancasComPermissao = bancas.filter(banca => hasPermissionForBanca(banca.id));

  const handleBancaClick = (banca: BancaData, event: React.MouseEvent) => {
    const permissoes = getPermissoesUsuario(banca.id);
    
    // Ordem de precedência:
    // 1. Presidente: ABRIR_VOTACAO
    if (permissoes.includes(`${PermissaoEnum.ABRIR_VOTACAO}-${banca.id}`)) {
      handleAcessarBancaPresidente(banca.id);
      return;
    }
    
    // 2. Membro: VOTAR_CANDIDATO (sem ABRIR_VOTACAO)
    if (permissoes.includes(`${PermissaoEnum.VOTAR_CANDIDATO}-${banca.id}`)) {
      handleAcessarBancaMembro(banca.id);
      return;
    }
    
    // 3. Vincular Candidato: VINCULAR_CANDIDATO
    if (permissoes.includes(`${PermissaoEnum.VINCULAR_CANDIDATO}-${banca.id}`)) {
      navegarParaInserirCandidatos(banca, event);
      return;
    }
    
    // 4. Secretário: FINALIZAR_BANCA (sem ABRIR_VOTACAO)
    if (permissoes.includes(`${PermissaoEnum.FINALIZAR_BANCA}-${banca.id}`)) {
      abrirAcoesSecretario(banca, event);
      return;
    }
  };

  const shouldRenderBotaoComAbrirVotacao = (bancaId: string): boolean => {
    return getPermissoesUsuario(bancaId).includes(`${PermissaoEnum.ABRIR_VOTACAO}-${bancaId}`);
  };

  const shouldRenderBotaoSomenteVotar = (bancaId: string): boolean => {
    const permissoes = getPermissoesUsuario(bancaId);
    return permissoes.includes(`${PermissaoEnum.VOTAR_CANDIDATO}-${bancaId}`) && 
           !permissoes.includes(`${PermissaoEnum.ABRIR_VOTACAO}-${bancaId}`);
  };

  const hasVincularCandidatoPermission = (bancaId: string): boolean => {
    return getPermissoesUsuario(bancaId).includes(`${PermissaoEnum.VINCULAR_CANDIDATO}-${bancaId}`);
  };
  
  if (!isOpen) return null;

  const formatDateTime = (data: string, horaInicial: string) => {
    if (!data || !horaInicial) return 'Data não disponível';
    try {
      const dateTime = `${data}T${horaInicial}`;
      return format(parseISO(dateTime), 'dd/MM/yyyy HH:mm', { locale: ptBR });
    } catch {
      return 'Data inválida';
    }
  };

  const handleAcessarBanca = (bancaId: string) => {
    navigate(`/bancas/${bancaId}/heteroidentificacao-membro`);
    onClose();
  };

  const handleAcessarBancaPresidente = (bancaId: string) => {
    navigate(`/bancas/${bancaId}/heteroidentificacao-presidente`);
    onClose();
  };

  const handleAcessarBancaMembro = (bancaId: string) => {
    navigate(`/bancas/${bancaId}/heteroidentificacao-membro`);
    onClose();
  };

  const handleCopy = (link: string, event: React.MouseEvent) => {
    event.stopPropagation();
    navigator.clipboard.writeText(link);
    toast.success('Url copiada.', {
      position: "top-right",
      autoClose: 1600,
      hideProgressBar: true,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      theme: "light",
    });
  };

  const navegarParaInserirCandidatos = (banca: BancaData, event: React.MouseEvent) => {
    event.stopPropagation();
    navigate(`/processos/${banca.processoId}/bancas/${banca.id}/inserir-candidatos`);
  };

  const abrirAcoesSecretario = (banca: BancaData, event: React.MouseEvent) => {
    event.stopPropagation();
    setSelectedBancaId(banca.id);
    setSelectedBanca(banca);
    setShowAcoesSecretarioModal(true);
  };

  const handleBancaFinalizada = () => {
    // Callback para atualizar a lista de bancas após finalização
    onClose();
  };

  const handleMinimize = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setIsMinimized(true);
      setIsTransitioning(false);
    }, 300);
  };

  const handleRestore = () => {
    setIsTransitioning(true);
    setIsMinimized(false);
    setTimeout(() => {
      setIsTransitioning(false);
    }, 300);
  };

  if (isMinimized) {
    return (
      <div 
        style={{
          position: 'fixed',
          top: isTransitioning ? '50vh' : '120px',
          right: isTransitioning ? '50vw' : '20px',
          zIndex: 1050,
          cursor: 'pointer',
          transform: isTransitioning ? 'scale(3) translate(50%, 50%)' : 'scale(1)',
          transformOrigin: 'top right',
          opacity: isTransitioning ? 0.8 : 1,
          transition: 'all 0.5s cubic-bezier(0.23, 1, 0.32, 1)'
        }}
        onClick={handleRestore}
      >
        <div 
          className="card shadow-lg" 
          style={{ 
            width: '280px', 
            backgroundColor: '#2c3e50', 
            color: 'white',
            border: '4px solid transparent',
            backgroundImage: 'linear-gradient(#2c3e50, #2c3e50), linear-gradient(45deg, #7fb3d3, #a8d8a8, #8fbc8f, #98d982, #a8d8a8)',
            backgroundOrigin: 'border-box',
            backgroundClip: 'content-box, border-box',
            backgroundSize: 'auto, 400% 400%',
            animation: 'borderMove 4s ease-in-out infinite'
          }}
        >
          <div className="card-body py-2 px-3">
            <div className="d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center">
                <i className="bi bi-people me-2"></i>
                <small className="fw-bold">Bancas de hoje ({bancasComPermissao.length})</small>
              </div>
              <div className="d-flex gap-1">
                <i className="bi bi-window-fullscreen" style={{ fontSize: '0.8rem' }}></i>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="modal fade show" 
      style={{ 
        display: 'block', 
        backgroundColor: `rgba(0,0,0,${isAnimating ? '0.5' : '0'})`,
        zIndex: 1050,
        transition: 'background-color 0.5s ease'
      }} 
      tabIndex={-1}
      onClick={handleMinimize}
    >
      <div 
        className="modal-dialog modal-lg" 
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'relative',
          top: isTransitioning ? '-30vh' : '0',
          left: isTransitioning ? '30vw' : '0',
          transform: isTransitioning ? 'scale(0.3)' : `scale(${isAnimating ? '1' : '1'})`,
          transformOrigin: 'center center',
          opacity: isTransitioning ? 0.8 : (isAnimating ? 1 : 0),
          transition: 'all 0.5s cubic-bezier(0.23, 1, 0.32, 1)'
        }}
      >
        <div className="modal-content">
          <div className="modal-header d-flex justify-content-between align-items-center">
            <h5 className="modal-title">Bancas de hoje</h5>
            <button 
              type="button" 
              className="btn btn-link p-0 text-secondary"
              onClick={handleMinimize}
              title="Minimizar"
              style={{ fontSize: '1.2rem', lineHeight: 1 }}
            >
              <i className="bi bi-dash-square"></i>
            </button>
          </div>
          <div className="modal-body">
                {bancasComPermissao.length === 0 ? (
                  <p>Nenhuma banca disponível no momento.</p>
                ) : (
                  <div className="list-group list-group-flush">
                    {bancasComPermissao.map((banca) => (
                      <div 
                        key={banca.id} 
                        className="list-group-item" 
                        style={{ 
                          borderLeft: banca.dataFinalizacao ? '4px solid #28a745' : '4px solid #007bff', 
                          cursor: 'pointer',
                          backgroundColor: banca.dataFinalizacao ? '#f8fff8' : 'white'
                        }}
                        onClick={(e) => handleBancaClick(banca, e)}
                      >
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <div className="badge bg-light text-dark border px-3 py-2">
                            <strong>Processo:</strong> {banca.processoNome || 'N/A'}
                          </div>
                          <div className="d-flex align-items-center gap-2">
                            <PermissaoBancaWrapper permissoesRequiridas={[PermissaoEnum.VINCULAR_CANDIDATO]} bancaId={banca.id}>
                              <button
                                className="btn btn-outline-secondary btn-sm"
                                title="Associar Candidatos à Banca"
                                onClick={(event) => navegarParaInserirCandidatos(banca, event)}
                              >
                                <i className="bi bi-wrench"></i>
                              </button>
                            </PermissaoBancaWrapper>
                            <PermissaoBancaWrapper permissoesRequiridas={[PermissaoEnum.FINALIZAR_BANCA]} bancaId={banca.id}>
                              <button
                                className="btn btn-outline-success btn-sm"
                                title="Ações do Secretário"
                                onClick={(event) => abrirAcoesSecretario(banca, event)}
                              >
                                <i className="bi bi-patch-check-fill"></i>
                              </button>
                            </PermissaoBancaWrapper>

                          </div>
                        </div>
                        <div className="d-flex align-items-center mb-2">
                          <div className="me-3 text-primary" style={{ fontSize: '1.5rem' }}>
                            <i className="bi bi-people"></i>
                          </div>
                          <div>
                            <h6 className="mb-1">{banca.tipoBanca || 'Tipo não disponível'}</h6>
                            <small className="text-muted">
                              <i className="bi bi-calendar3 me-1"></i>
                              {formatDateTime(banca.data, banca.horaInicial)}
                            </small>
                          </div>
                        </div>
                        <div className="d-flex align-items-center justify-content-between">
                          <div className="d-flex align-items-center">
                            <i className="bi bi-camera-video me-2 text-secondary"></i>
                            <small className="text-truncate" style={{ maxWidth: '300px' }}>
                              {banca.salaDeBanca || 'Link não disponível'}
                            </small>
                            {banca.salaDeBanca && (
                              <button 
                                className="btn btn-outline-primary btn-sm ms-2"
                                title="Copiar Link"
                                onClick={(event) => handleCopy(banca.salaDeBanca, event)}
                              >
                                <i className="bi bi-link-45deg"></i>
                              </button>
                            )}
                          </div>
                          <div className="d-flex align-items-center gap-2">
                            {banca.dataFinalizacao ? (
                              <div className="d-flex align-items-center text-success">
                                <i className="bi bi-check-circle-fill me-1"></i>
                                <small className="fw-bold">FINALIZADA</small>
                              </div>
                            ) : (
                              <PermissaoBancaWrapper permissoesRequiridas={[PermissaoEnum.VOTAR_CANDIDATO]} bancaId={banca.id}>
                                <small className="text-success fw-bold">
                                  <i className="bi bi-arrow-right-circle me-1"></i>
                                  Clique para acessar →
                                </small>
                              </PermissaoBancaWrapper>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
          </div>

        </div>
      </div>
      
      <AcoesSecretarioModal
        isOpen={showAcoesSecretarioModal}
        onClose={() => setShowAcoesSecretarioModal(false)}
        bancaId={selectedBancaId}
        onBancaFinalizada={handleBancaFinalizada}
        banca={selectedBanca}
      />
    </div>
  );
};

export default BancasModal;